import { snapshot as valtioSnapshot, subscribe as valtioSubscribe, type Snapshot } from 'valtio/vanilla'

interface ValtioRuntime {
  snapshot: typeof valtioSnapshot
  subscribe: typeof valtioSubscribe
}

export interface StateOwner {
  snapshot: () => unknown
  subscribe: (callback: () => void) => () => void
}

interface StateGlobals {
  __ZQA_STATE__?: Record<string, object>
  __ZQA_STATE_VALTIO_OWNERS__?: WeakMap<object, StateOwner>
}

// Valtio only deep-tracks plain objects/arrays; a nested Map/Set/Date/class
// instance mutates invisibly, so subscribers silently go stale with no error.
const isPlainValue = (value: unknown): boolean => {
  if (value === null || typeof value !== 'object') return true

  const proto = Object.getPrototypeOf(value)
  if (proto !== Object.prototype && proto !== Array.prototype && proto !== null) return false

  return Object.values(value as object).every(isPlainValue)
}

// Each bundle has its own Valtio WeakMaps. The proxy and these owner-bound
// operations must therefore be shared together.
export const createSharedStateRegistry = (
  registry: Record<string, object>,
  owners: WeakMap<object, StateOwner>,
  runtime: ValtioRuntime,
) => {
  const getOwner = (state: object) => {
    const owner = owners.get(state)
    if (!owner) throw new Error('Shared state was not created with getSharedState')
    return owner
  }

  return {
    getSharedState<T extends object>(key: string, create: () => T): T {
      const existing = registry[key]
      if (existing) return existing as T

      const state = create()

      if (process.env.NODE_ENV !== 'production' && !isPlainValue(state)) {
        throw new Error(
          `getSharedState('${key}') received a non-plain value (Map/Set/Date/class instance). ` +
            'Valtio only deep-tracks plain objects/arrays; nested mutations inside a non-plain ' +
            'value will not notify subscribers, so consumers will silently go stale.',
        )
      }

      owners.set(state, {
        snapshot: () => runtime.snapshot(state),
        subscribe: (callback) => runtime.subscribe(state, callback, true),
      })
      registry[key] = state
      return state
    },
    snapshot<T extends object>(state: T): Snapshot<T> {
      return getOwner(state).snapshot() as Snapshot<T>
    },
    subscribe<T extends object>(state: T, callback: () => void): () => void {
      return getOwner(state).subscribe(callback)
    },
  }
}

const globals = globalThis as StateGlobals
const registry = (globals.__ZQA_STATE__ ??= {})
const owners = (globals.__ZQA_STATE_VALTIO_OWNERS__ ??= new WeakMap())

export const { getSharedState, snapshot, subscribe } = createSharedStateRegistry(registry, owners, {
  snapshot: valtioSnapshot,
  subscribe: valtioSubscribe,
})
